const STORAGE_KEY = "classPoints.clientErrors.v1";
const MAX_QUEUE = 20;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type ClientErrorPayload = {
  client: "web";
  appVersion: string;
  platform: string;
  errorType: string;
  message: string;
  stack?: string;
  route?: string;
  operation?: string;
  occurredAt: string;
};

type QueuedError = { queuedAt: number; payload: ClientErrorPayload };

let flushing = false;
let installed = false;

function store() {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

export function sanitizeClientErrorText(value: unknown, limit: number) {
  return String(value ?? "")
    .replace(/(https?:\/\/[^\s?#]+)[?#][^\s]*/gi, "$1?<redacted>")
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer <redacted>")
    .replace(/\b(authorization|cookie|x-csrf-token|csrf(?:token)?|session|password|token)(\s*[:=]\s*)([^\s,;"']+)/gi, "$1$2<redacted>")
    .replace(/(?:\/Users\/|\/home\/)[^/\s]+|[A-Za-z]:\\Users\\[^\\\s]+/g, "<home>")
    .slice(0, limit);
}

function normalizedRoute(value: string) {
  return sanitizeClientErrorText(value.split("?")[0].replace(/\/\d+(?=\/|$)/g, "/:id"), 120);
}

function loadQueue(now = Date.now()): QueuedError[] {
  const storage = store();
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) || "[]") as QueuedError[];
    const queue = parsed.filter((item) => item && now - Number(item.queuedAt) <= MAX_AGE_MS).slice(-MAX_QUEUE);
    if (queue.length !== parsed.length) saveQueue(queue);
    return queue;
  } catch {
    return [];
  }
}

function saveQueue(queue: QueuedError[]) {
  const storage = store();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch {
    // Telemetry must never interfere with the application.
  }
}

function createPayload(input: Partial<ClientErrorPayload> & Pick<ClientErrorPayload, "errorType" | "message">): ClientErrorPayload {
  const location = typeof window === "undefined" ? "" : window.location.pathname;
  return {
    client: "web",
    appVersion: sanitizeClientErrorText(input.appVersion || import.meta.env.VITE_APP_VERSION || "web", 120),
    platform: sanitizeClientErrorText(input.platform || globalThis.navigator?.userAgent || "browser", 120),
    errorType: sanitizeClientErrorText(input.errorType, 120),
    message: sanitizeClientErrorText(input.message, 500),
    stack: sanitizeClientErrorText(input.stack || "", 4000) || undefined,
    route: normalizedRoute(input.route || location) || undefined,
    operation: sanitizeClientErrorText(input.operation || "", 120) || undefined,
    occurredAt: input.occurredAt || new Date().toISOString()
  };
}

export async function flushClientErrors() {
  if (flushing || typeof fetch === "undefined") return;
  const queue = loadQueue();
  if (!queue.length) return;
  flushing = true;
  try {
    const remaining = [...queue];
    while (remaining.length) {
      const response = await fetch("/api/client-errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(remaining[0].payload)
      });
      if (!response.ok) break;
      remaining.shift();
      saveQueue(remaining);
    }
  } catch {
    // Keep the queue for the next startup or successful request.
  } finally {
    flushing = false;
  }
}

export function reportClientError(input: Partial<ClientErrorPayload> & Pick<ClientErrorPayload, "errorType" | "message">) {
  const queue = loadQueue();
  queue.push({ queuedAt: Date.now(), payload: createPayload(input) });
  saveQueue(queue);
  void flushClientErrors();
}

export function reportApiFailure(path: string, status: number, _message?: string) {
  if (status < 500 || path === "/api/client-errors") return;
  reportClientError({
    errorType: "ApiError",
    message: `HTTP ${status}`,
    route: path,
    operation: "api.request"
  });
}

export function installClientErrorReporting() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event) => {
    reportClientError({
      errorType: event.error?.name || "WindowError",
      message: "Unhandled window error",
      stack: event.error?.stack?.split("\n").slice(1).join("\n")
    });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    reportClientError({
      errorType: reason instanceof Error ? reason.name : "UnhandledRejection",
      message: "Unhandled promise rejection",
      stack: reason instanceof Error ? reason.stack?.split("\n").slice(1).join("\n") : undefined
    });
  });
  void flushClientErrors();
}

export function resetClientErrorReporterForTests() {
  flushing = false;
  installed = false;
  store()?.removeItem(STORAGE_KEY);
}
